import { convertImageToAscii } from "../components/Assistant/ImageToAscii";
import { ChatResponseDTO, ChatResponse } from "../Types/Chat/ChatResponse";
import { getAnimatedText } from "../components/Assistant/AnimatedText";
import { ReactNode } from "react";
import { ChatSessionItem } from "../Types/Chat/ChatSessionItem";
import { ChatProgress, StartChatResponse } from "../Types/Chat/ChatProgress";

const POLL_INTERVAL_MS = 3_000;

const wait = (milliseconds: number, signal?: AbortSignal): Promise<void> =>
    new Promise((resolve, reject) => {
        const onAbort = () => {
            window.clearTimeout(timeoutId);
            reject(signal?.reason ?? new DOMException("The operation was aborted", "AbortError"));
        };
        const timeoutId = window.setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, milliseconds);

        if (signal?.aborted) {
            onAbort();
            return;
        }

        signal?.addEventListener("abort", onAbort, { once: true });
    });

const mapChatResponse = (response: ChatResponseDTO): ChatResponse => ({
    id: response.id,
    message: response.responseMessage,
    toolsCalled: response.toolsCalled,
    elements: [getAnimatedText(response.responseMessage, true)],
    role: response.role,
    canBeRated: response.canBeRated,
    sessionId: response.sessionId,
    queryId: response.queryId
});

export function getPendingQueryId(history: ChatResponse[]): string | undefined {
    for (let index = history.length - 1; index >= 0; index--) {
        const message = history[index];
        if (message.role === "assistant") {
            return undefined;
        }
        if (message.role === "user" && message.queryId) {
            return message.queryId;
        }
    }

    return undefined;
}

async function pollChatProgress(
    statusUrl: string,
    onProgress: (progress: ChatProgress) => void,
    signal?: AbortSignal
): Promise<ChatResponse> {
    while (true) {
        const progressResponse = await fetch(statusUrl, { signal });

        if (progressResponse.status === 404) {
            throw new Error("The chat request was not found or has expired.");
        }
        if (!progressResponse.ok) {
            throw new Error(`Could not retrieve chat progress (${progressResponse.status})`);
        }

        const progress: ChatProgress = await progressResponse.json();
        onProgress(progress);

        if (progress.state === "Completed") {
            if (!progress.result) {
                throw new Error("The completed chat request did not include a response");
            }
            return mapChatResponse(progress.result);
        }
        if (progress.state === "Failed") {
            throw new Error(progress.error ?? "The chat request could not be completed.");
        }

        await wait(POLL_INTERVAL_MS, signal);
    }
}

export async function getAssistantChat(message: string, id: string): Promise<ChatResponse> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/chat?message=${message}&id=${id}`;

    const messageLower = message.trim().toLowerCase();


    if (messageLower.includes("temperature on the moon") || (messageLower.includes("temperaturen på månen"))) {
        const ascii = await convertImageToAscii();
        const textClass = "text-sm/[6px] font-mono";
        return {
            id: "secret message",
            message: ascii,
            role: "assistant",
            toolsCalled: [],
            elements: [getAnimatedText(ascii, true, textClass)],
            canBeRated: false,
            sessionId: -1
        };
    }

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const responseText: ChatResponseDTO = await response.json();
            const elements: ReactNode[] = [getAnimatedText(responseText.responseMessage, true)];
            return {
                id: responseText.id,
                message: responseText.responseMessage,
                toolsCalled: responseText.toolsCalled,
                elements,
                role: responseText.role,
                canBeRated: responseText.canBeRated,
                sessionId: responseText.sessionId
            };
        } else {
            console.error("Failed to fetch data", response.status);
            return UnavailableResponse();
        }
    } catch (error) {
        console.error("Failed to fetch data", error);
        return UnavailableResponse();
    }
}

export async function postChatMessage(
    userId: string,
    sessionId: number,
    message: string,
    onProgress: (progress: ChatProgress) => void = () => undefined,
    signal?: AbortSignal
): Promise<ChatResponse> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    if (!apiUrl) {
        throw new Error("Chat API URL is not configured");
    }

    const startResponse = await fetch(
        `${apiUrl}/chat/async?sessionId=${sessionId}&userId=${encodeURIComponent(userId)}`,
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ message }),
            signal
        }
    );

    if (startResponse.status === 503) {
        throw new Error("The assistant is busy. Please try again shortly.");
    }
    if (startResponse.status !== 202) {
        throw new Error(`Could not start chat request (${startResponse.status})`);
    }

    const started: StartChatResponse = await startResponse.json();
    if (!started.queryId || !started.statusUrl) {
        throw new Error("The chat request returned an invalid polling response");
    }

    const statusUrl = /^https?:\/\//i.test(started.statusUrl)
        ? started.statusUrl
        : `${apiUrl.replace(/\/+$/, "")}/${started.statusUrl.replace(/^\/+/, "")}`;
    return pollChatProgress(statusUrl, onProgress, signal);
}

export async function getChatProgress(
    userId: string,
    sessionId: number,
    queryId: string,
    onProgress: (progress: ChatProgress) => void = () => undefined,
    signal?: AbortSignal
): Promise<ChatResponse> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    if (!apiUrl) {
        throw new Error("Chat API URL is not configured");
    }

    const statusUrl = `${apiUrl}/chat/progress/${encodeURIComponent(queryId)}?sessionId=${sessionId}&userId=${encodeURIComponent(userId)}`;
    return pollChatProgress(statusUrl, onProgress, signal);
}

export async function getAssistantGreetings(): Promise<ChatResponse> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/greeting`;

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const responseText = await response.text();
            const elements: ReactNode[] = [getAnimatedText(responseText, true)];
            return {
                id: "0",
                message: responseText,
                toolsCalled: [],
                elements,
                role: "assistant",
                canBeRated: false,
                sessionId: -1
            };
        } else {
            console.error("Failed to fetch data", response.status);
            return UnavailableResponse();
        }
    } catch (error) {
        console.error("Failed to fetch data", error);
        return UnavailableResponse();
    }
}

export async function getChatHistory(userId: string): Promise<ChatResponse[]> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/chatHistory?id=${userId}`;

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const responseText: ChatResponseDTO[] = await response.json();
            return responseText.map((response) => {
                return {
                    id: response.id,
                    role: response.role,
                    message: response.responseMessage,
                    toolsCalled: response.toolsCalled,
                    elements: [getAnimatedText(response.responseMessage, false)],
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId,
                    queryId: response.queryId,
                };
            });
        } else {
            console.error("Failed to fetch data", response.status);
            return [UnavailableResponse()];
        }
    } catch (error) {
        console.error("Failed to fetch data", error);
        return [UnavailableResponse()];
    }
}

export async function getChatHistoryBySession(userId: string, sessionId: number): Promise<ChatResponse[]> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/chatHistory?userId=${userId}&sessionId=${sessionId}`;

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const responseText: ChatResponseDTO[] = await response.json();
            return responseText.map((response) => {
                return {
                    id: response.id,
                    role: response.role,
                    message: response.responseMessage,
                    toolsCalled: response.toolsCalled,
                    elements: [getAnimatedText(response.responseMessage, false)],
                    canBeRated: response.canBeRated,
                    sessionId: response.sessionId,
                    queryId: response.queryId,
                };
            });
        } else {
            console.error("Failed to fetch data", response.status);
            return [UnavailableResponse()];
        }
    } catch (error) {
        console.error("Failed to fetch data", error);
        return [UnavailableResponse()];
    }
}

export async function clearChatHistory(id: number): Promise<boolean> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/clearHistory?id=${id}`;

    try {
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        });
        return response.ok;
    } catch (error) {
        console.error("Failed to fetch data", error);
        return false;
    }
}
export async function newChatSession(userId: string): Promise<number> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/newSession?userId=${userId}`;

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const sessionId: number = Number(await response.text());
            return sessionId;
        } else {
            console.error("Failed to create new chat session", response.status);
            return -1;
        }
    } catch (error) {
        console.error("Failed to create new chat session", error);
        return -1;
    }
}

export async function getSessionList(userId: string): Promise<ChatSessionItem[]> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    console.log("User ID: ", userId);
    const url = `${apiUrl}/GetSessions?Id=${userId}`;
    console.log("URL: ", url);
    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });
        if (response.ok) {
            const sessionList: ChatSessionItem[] = await response.json();
            return sessionList;
        } else {
            console.error("Failed to fetch session list", response.status);
            return [];
        }
    } catch (error) {
        console.error("Failed to fetch session list", error);
        return [];
    }
}

export async function rateResponse(sessionId: number, rating: number, messageId: string, message: string): Promise<void> {
    const apiUrl = process.env.REACT_APP_CHAT_API_URL;
    const url = `${apiUrl}/PostRating`;
    console.log("Rating response", sessionId, rating, messageId, message);
    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                sessionId,
                messageId,
                rating,
                message,
            }),
        });
        if (!response.ok) {
            console.error("Failed to rate response", response.status);
        }
    } catch (error) {
        console.error("Failed to rate response", error);
    }
}

function UnavailableResponse(): ChatResponse {
    const errorMessage = "I'm currently not available, please try again later.";
    const animatedErrorText = getAnimatedText(errorMessage, false);
    return {
        id: "error",
        message: errorMessage,
        role: "assistant",
        toolsCalled: [],
        elements: [animatedErrorText],
        canBeRated: false,
        sessionId: -1
    };
}